import { useEffect, useState, ReactNode } from 'react';
import { fetchTopics } from '@api/fetch-topics';
import { IMessage, INlpResponse, ITopic, ITopicGroup, TopicStatus } from '@common/types';
import { AGREEMENT_BANNER_STORAGE_KEY } from '@constants/local-storage';
import useLocalStorage from '@hooks/use-local-storage';
import { CURRENT_INSTANCE } from '@lib/utils';
import ChatProviderContext from '@stores/contexts/chat-context';

interface IChatProviderProps {
  children: ReactNode;
}

function ChatProvider({ children, ...props }: IChatProviderProps) {
  const [topics, setTopics] = useState<ITopic[]>([]);
  const [topicGroups, setTopicGroups] = useState<ITopicGroup[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [responses, setResponses] = useState<Array<{ id: number; response: INlpResponse }>>([]);
  const [showAgreementBanner, setShowAgreementBanner] = useState<boolean>(false);
  const [agreedToDisclaimer, setAgreedToDisclaimer] = useLocalStorage<boolean>(
    AGREEMENT_BANNER_STORAGE_KEY,
    false
  );
  const [agreedToDisclaimerPerSession, setAgreedToDisclaimerPerSession] = useState<boolean>(false);

  useEffect(() => {
    if (topics.length === 0) {
      fetchTopics()
        .then((result) => {
          if (result) {
            if (CURRENT_INSTANCE.topics.length === 0) {
              const allowedTopics = result.filter((topic) => TopicStatus.Inactive !== topic.status);
              setTopics(allowedTopics);
              const allTopicGroups: ITopicGroup[] = allowedTopics
                .filter((topic) => topic.groupId)
                .map((topic) => ({
                  id: topic.groupId!,
                  name: topic.groupName!,
                  synonyms: topic.groupSynonyms,
                }));
              const uniqueTopicGroups = Array.from(
                new Map(allTopicGroups.map((item) => [item.id, item])).values()
              );
              setTopicGroups(uniqueTopicGroups);
            } else {
              const allowedTopics = result
                .filter((topic) => TopicStatus.Inactive !== topic.status)
                .filter((topic) => CURRENT_INSTANCE.topics.includes(topic.name.toLowerCase()));
              setTopics(allowedTopics);
              const allTopicGroups: ITopicGroup[] = allowedTopics
                .filter((topic) => topic.groupId)
                .map((topic) => ({
                  id: topic.groupId!,
                  name: topic.groupName!,
                  synonyms: topic.groupSynonyms,
                }));
              const uniqueTopicGroups = Array.from(
                new Map(allTopicGroups.map((item) => [item.id, item])).values()
              );
              setTopicGroups(uniqueTopicGroups);
            }
          }
        })
        .catch(console.error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    topics,
    topicGroups,
    selectedTopic,
    setSelectedTopic,
    messages,
    addMessage: (message: IMessage) => setMessages((prev) => [...prev, message]),
    responses,
    addResponse: (id: number, response: INlpResponse) =>
      setResponses((prev) => [...prev, { id, response }]),
    agreedToDisclaimer,
    setAgreedToDisclaimer,
    agreedToDisclaimerPerSession,
    setAgreedToDisclaimerPerSession,
    showAgreementBanner,
    setShowAgreementBanner,
  };

  return (
    <ChatProviderContext.Provider {...props} value={value}>
      {children}
    </ChatProviderContext.Provider>
  );
}

export default ChatProvider;
